import * as THREE from 'three/webgpu';
import {
  uniform as tslUniform,
  Fn,
  positionWorld,
  materialOpacity,
  vec2,
  vec4,
  float,
  dot,
  length,
  smoothstep,
  mix,
  hash,
  max as tslMax,
} from 'three/tsl';

/**
 * Turn a classic material into its node equivalent, preserving what matters.
 *
 * `WorldMask.applyToMaterial` can only work on node materials — the classic
 * `THREE.Mesh*Material` classes do not set `isNodeMaterial`, and `three/webgpu`
 * re-exports those same classes, so a GLTF-loaded scene is full of materials the
 * mask silently skips. On a course that meant flagsticks, houses and anything
 * else the lightmap pass did not happen to touch rendering straight through the
 * player's walls.
 *
 * Returns the original when it is already a node material, or when the type is
 * not one this knows how to carry across — the caller should treat "same object
 * back" as "not convertible".
 *
 * The alpha properties are not optional extras: tree billboards are cut out with
 * `alphaTest`/`alphaToCoverage` (see TreePlanter), and dropping those turns every
 * impostor into an opaque rectangle.
 */
export function toNodeMaterial(material: THREE.Material): THREE.Material {
  const source: any = material;
  if (source.isNodeMaterial) return material;

  const Ctor = source.isMeshStandardMaterial || source.isMeshPhysicalMaterial
    ? THREE.MeshStandardNodeMaterial
    : source.isMeshBasicMaterial
      ? THREE.MeshBasicNodeMaterial
      : undefined;
  if (!Ctor) return material;

  const target: any = new Ctor();
  target.name = source.name;
  // Shared surface properties. Copied by hand rather than via .copy(), which
  // drags across the classic material's internal shader flags too.
  for (const key of [
    'map', 'color', 'opacity', 'transparent', 'alphaMap', 'alphaTest',
    'alphaToCoverage', 'side', 'depthWrite', 'depthTest', 'vertexColors',
    'toneMapped', 'fog', 'wireframe', 'visible', 'blending', 'premultipliedAlpha',
  ]) {
    if (source[key] !== undefined) target[key] = source[key];
  }
  if (Ctor === THREE.MeshStandardNodeMaterial) {
    for (const key of [
      'roughness', 'metalness', 'roughnessMap', 'metalnessMap', 'normalMap',
      'normalScale', 'aoMap', 'aoMapIntensity', 'emissive', 'emissiveMap',
      'emissiveIntensity', 'envMapIntensity', 'flatShading',
    ]) {
      if (source[key] !== undefined) target[key] = source[key];
    }
  }
  target.needsUpdate = true;
  return target;
}

export type WorldMaskOptions = {
  /** Distance (m) downrange of the anchor where the virtual world begins. */
  portalDistance?: number;
  /** Feather width (m) of the portal edge on non-ground geometry. */
  portalFeather?: number;
  /** Feather width (m) of the portal edge on ground surfaces. */
  groundFeather?: number;
  /** Passthrough floor ellipse around the mat: [lateral, downrange] radii (m). */
  discRadii?: [number, number];
  /** Feather width (m) of the floor ellipse edge. */
  discFeather?: number;
  /** Optional lateral bounds on the virtual world. Off by default. */
  side?: { halfWidth: number; spread: number; feather: number } | null;
  /**
   * 'punch': opaque punch-through — materials stay opaque (no sorting) and a
   * custom blend writes premultiplied rgb·mask / a=mask, so passthrough shows
   * with no halos. 'dither': world-anchored hashed alpha-test — binary alpha,
   * no dependency on compositor alpha semantics (fallback).
   */
  featherMode?: 'punch' | 'dither';
};

type ApplyMaterialOptions = {
  ground?: boolean;
  mode?: 'punch' | 'dither' | 'compose';
};

/** The rectangular opening the world is seen through, anchor-relative. */
export type WindowRect = {
  /** Window plane, metres downrange of the anchor (usually the far wall). */
  dist: number;
  /** |lateral| distance to the left wall (left of the aim line). */
  left: number;
  /** …and to the right wall. */
  right: number;
  /** Ceiling height above the anchor's floor. */
  top: number;
};

/**
 * Golf+-style MR compositing mask: the virtual world exists only beyond a
 * portal plane downrange of the anchored ball, with a passthrough floor
 * ellipse around the player's mat feathering into the virtual fairway.
 *
 * Anchor-relative by design — the FRAME is a function of world position and
 * the anchor pose only, fixed in the room; it must not move as the player
 * looks around. Since the anchored worldRoot maps virtual coords onto
 * physical space, positionWorld is already the right domain; no extra matrix
 * needed.
 *
 * The optional WINDOW term is the one deliberate exception to "head pose
 * never enters": a real window's frame is fixed, but WHAT you see through it
 * depends on where your eye is — that parallax is exactly what makes an
 * opening read as a window instead of a texture. So the window test projects
 * the fragment back toward the eye and asks whether the ray crosses the
 * window rectangle (side walls left/right, ceiling top, floor bottom). The
 * eye is the per-frame MIDPOINT of the two XR cameras — the per-eye
 * `cameraPosition` built-in is unusable in r185 XR (see BallTrailOptions.
 * viewerFade) and per-eye masks would cause edge rivalry anyway; the feather
 * absorbs the ~1.6 cm midpoint-vs-eye disparity at the frame edges.
 */
export class WorldMask {
  readonly uEnabled = tslUniform(0);
  readonly uAnchor = tslUniform(new THREE.Vector2(0, 0));
  readonly uAnchorY = tslUniform(0);
  readonly uFwd = tslUniform(new THREE.Vector2(0, 1));
  readonly uPortalDist = tslUniform(1.2);
  readonly uPortalFeather = tslUniform(0.6);
  readonly uGroundFeather = tslUniform(1.0);
  readonly uDiscRadii = tslUniform(new THREE.Vector2(1.6, 2.2));
  readonly uDiscFeather = tslUniform(0.8);
  readonly uSideEnabled = tslUniform(0);
  readonly uSideHalfWidth = tslUniform(30);
  readonly uSideSpread = tslUniform(1.2);
  readonly uSideFeather = tslUniform(8);
  readonly uWindowEnabled = tslUniform(0);
  /** Eye midpoint in reference space — fed every frame while presenting. */
  readonly uEye = tslUniform(new THREE.Vector3(0, 1.6, -2));
  readonly uWinDist = tslUniform(3.5);
  readonly uWinLeft = tslUniform(2.2);
  readonly uWinRight = tslUniform(2.2);
  readonly uWinTop = tslUniform(2.4);
  readonly uWinFeather = tslUniform(0.25);

  featherMode: 'punch' | 'dither';

  #maskNodes: { ground?: any; world?: any } = {};
  #applied: THREE.Material[] = [];

  constructor(options: WorldMaskOptions = {}) {
    if (options.portalDistance !== undefined) this.uPortalDist.value = options.portalDistance;
    if (options.portalFeather !== undefined) this.uPortalFeather.value = options.portalFeather;
    if (options.groundFeather !== undefined) this.uGroundFeather.value = options.groundFeather;
    if (options.discRadii) this.uDiscRadii.value.set(options.discRadii[0], options.discRadii[1]);
    if (options.discFeather !== undefined) this.uDiscFeather.value = options.discFeather;
    this.featherMode = options.featherMode ?? 'punch';
    if (options.side) {
      this.uSideEnabled.value = 1;
      this.uSideHalfWidth.value = options.side.halfWidth;
      this.uSideSpread.value = options.side.spread;
      this.uSideFeather.value = options.side.feather;
    }
  }

  /** 0..1 visibility of the virtual world at positionWorld. Cached per variant. */
  maskNode(ground = false): any {
    const key = ground ? 'ground' : 'world';
    if (this.#maskNodes[key]) return this.#maskNodes[key];

    const node = Fn(() => {
      const rel: any = positionWorld.xz.sub(this.uAnchor);
      const fwd: any = dot(rel, this.uFwd);
      const lat: any = dot(rel, vec2(this.uFwd.y, this.uFwd.x.negate()));

      // Portal half-space: world visible only beyond the plane downrange
      const feather: any = ground ? this.uGroundFeather : this.uPortalFeather;
      let m: any = smoothstep(this.uPortalDist, this.uPortalDist.add(feather), fwd);

      if (ground) {
        // Passthrough bay around the mat — real turf feathers into fairway
        const e: any = length(vec2(lat.div(this.uDiscRadii.x), fwd.div(this.uDiscRadii.y)));
        const disc: any = smoothstep(float(1.0), float(1.0).add(this.uDiscFeather.div(this.uDiscRadii.y)), e);
        m = m.mul(disc);
      }

      // Optional soft side walls limiting lateral wrap
      const sideLimit: any = this.uSideHalfWidth.add(tslMax(fwd, float(0)).mul(this.uSideSpread));
      const side: any = smoothstep(sideLimit.add(this.uSideFeather), sideLimit, lat.abs());
      m = m.mul(mix(float(1.0), side, this.uSideEnabled));

      // Optional WINDOW: the fragment is visible only if the ray from the eye
      // to it crosses the window rectangle. Crossing point by similar
      // triangles on the anchor-frame forward axis; lat axis sign convention:
      // +lat is the player's LEFT when facing down the aim line.
      const eyeRel: any = this.uEye.xz.sub(this.uAnchor);
      const eyeFwd: any = dot(eyeRel, this.uFwd);
      const eyeLat: any = dot(eyeRel, vec2(this.uFwd.y, this.uFwd.x.negate()));
      const eyeY: any = this.uEye.y.sub(this.uAnchorY);
      const fragY: any = positionWorld.y.sub(this.uAnchorY);
      // Fragments nearer than the window plane are culled by the portal term
      // (the app keeps uPortalDist = window dist), so denom > 0 in the cases
      // that matter — the max() only guards degenerate geometry.
      const denom: any = fwd.sub(eyeFwd);
      const t: any = this.uWinDist.sub(eyeFwd).div(tslMax(denom, float(1e-3)));
      const crossLat: any = mix(eyeLat, lat, t);
      const crossY: any = mix(eyeY, fragY, t);
      const inLat: any = smoothstep(
        this.uWinRight.negate().sub(this.uWinFeather), this.uWinRight.negate(), crossLat
      ).mul(smoothstep(
        this.uWinLeft.add(this.uWinFeather), this.uWinLeft, crossLat
      ));
      const inY: any = smoothstep(this.uWinTop.add(this.uWinFeather), this.uWinTop, crossY)
        // Bottom edge sits at the floor; permissive so the fairway seam at the
        // portal never flickers against it
        .mul(smoothstep(float(-0.3), float(-0.05), crossY));
      m = m.mul(mix(float(1.0), inLat.mul(inY), this.uWindowEnabled));

      return mix(float(1.0), m, this.uEnabled);
    })();

    this.#maskNodes[key] = node;
    return node;
  }

  /**
   * Inject the mask into one material. Composes with existing opacityNode
   * setups (grass fades) and colorNode mutators (lightmap, yardage) — this
   * only touches opacityNode/alphaTestNode/blending, so run it last.
   */
  applyToMaterial(mat: THREE.Material, options: ApplyMaterialOptions = {}) {
    const m: any = mat;
    if (m.userData.mrMask) return;
    if (!m.isNodeMaterial) {
      console.warn('WorldMask: material is not a node material, skipping', mat.type);
      return;
    }

    // opacityNode is dead code on a material that writes its own fragment —
    // NodeMaterial never reaches the opacity multiply. Say so rather than
    // pretending it worked. (src/shaders/clouds.ts is the live example.)
    if (m.fragmentNode || m.outputNode) {
      console.warn(
        'WorldMask: material sets fragmentNode/outputNode, so opacity masking cannot apply',
        mat.type,
      );
    }

    const mask = this.maskNode(options.ground ?? false);
    const mode = options.mode
      ?? (m.transparent === true ? 'compose' : this.featherMode);

    const baseOpacity: any = m.opacityNode ? float(m.opacityNode) : materialOpacity;
    m.opacityNode = baseOpacity.mul(mask);

    /**
     * Always cut masked fragments, in every mode — and this is load-bearing for
     * a reason that has nothing to do with alpha.
     *
     * `needsUpdate` does NOT rebuild a node material's pipeline on the WebGPU
     * renderer. RenderObjects only rebuilds when `getCacheKey()` changes
     * (RenderObjects.js), and that key cannot see node graphs: it skips
     * `userData` and `version` outright and collapses any object-valued
     * property to the literal string '{}'. So swapping one opacityNode for
     * another is completely invisible to it — the material keeps its old
     * shader, and the mask is silently dropped.
     *
     * That is exactly what happened to water: MeshPhysicalNodeMaterial,
     * transparent, and it already had an opacityNode of its own, so masking it
     * changed '{}' to '{}' and nothing rebuilt. It rendered straight through
     * the player's walls no matter how many times the mask was re-applied.
     *
     * Setting alphaTestNode flips a property from null to a value, which the
     * key does see. It also happens to be the right thing on its own merits:
     * a masked fragment is dropped before shading rather than shaded and then
     * multiplied to nothing, which on something full-screen like water is real
     * fill rate. The threshold sits well under water's own 0.05 minimum
     * opacity, so nothing visible is lost.
     */
    /**
     * Compose with the material's own cutout threshold — never replace it.
     *
     * `alphaTestNode` OVERRIDES the `alphaTest` scalar outright in
     * NodeMaterial.setupDiffuseColor: it picks `alphaTestNode` when set and
     * `materialAlphaTest` only otherwise. So masking a cutout material used to
     * drop its threshold from 0.4/0.6 to 0.002, and every half-transparent
     * fringe texel in a foliage atlas — the ones the cutout exists to remove —
     * started rendering. Tree billboards came out ringed in halos, and on the
     * Low tier, where alphaToCoverage is off and `alphaTest` is the ONLY thing
     * cutting the quad out, they came out as near-solid rectangles.
     *
     * Taking the max keeps both jobs: the material still cuts its own texture
     * where it wants to, and masked fragments (alpha multiplied to ~0) are
     * still discarded before shading rather than shaded and multiplied away.
     */
    if (mode !== 'dither') m.alphaTestNode = float(Math.max(m.alphaTest ?? 0, 0.002));

    if (mode === 'punch' && m.transparent !== true) {
      // Opaque punch-through: stays in the opaque queue (depth writes on, no
      // sorting) but CustomBlending both escapes the node system's forced
      // diffuseColor.a = 1 (NodeBuilder.isOpaque requires NormalBlending) and
      // makes the blend unit emit premultiplied rgb·a / a — exactly what the
      // XR compositor and browser canvas compositing expect. mask=0 fragments
      // multiply their color to zero: no halos, MSAA-resolve safe.
      m.blending = THREE.CustomBlending;
      m.blendEquation = THREE.AddEquation;
      m.blendSrc = THREE.SrcAlphaFactor;
      m.blendDst = THREE.ZeroFactor;
      m.blendEquationAlpha = THREE.AddEquation;
      m.blendSrcAlpha = THREE.OneFactor;
      m.blendDstAlpha = THREE.ZeroFactor;
      // (the alpha test that stops hidden ground writing depth is set above,
      // for every mode)
    } else if (mode === 'dither' && m.transparent !== true) {
      // World-anchored stipple: identical in both eyes (screen-space dither
      // causes binocular rivalry) and stable under head motion.
      const noise: any = hash(
        positionWorld.x.mul(191.13).add(positionWorld.z.mul(313.71)).add(positionWorld.y.mul(97.41))
      );
      m.alphaTestNode = noise.mul(0.998).add(0.001);
    }

    m.userData.mrMask = mode;
    m.userData.mrMaskGround = options.ground ?? false;
    m.needsUpdate = true;
    this.#applied.push(mat);
  }

  /**
   * Walk a subtree and mask every mesh material. Exemption is explicit —
   * userData.mrMaskExempt or the exclude callback — rather than layer-based
   * (layers 1/2 are per-eye channels in XR).
   */
  apply(root: THREE.Object3D, options: {
    ground?: (mesh: THREE.Mesh) => boolean;
    exclude?: (object: THREE.Object3D) => boolean;
    /**
     * Convert classic materials to node materials instead of skipping them.
     *
     * Off by default because it REPLACES `mesh.material`, which is a visible
     * change to someone else's scene. On for anything loaded from a GLTF, where
     * the alternative is most of the scene quietly ignoring the mask.
     */
    convert?: boolean;
  } = {}): { masked: number; skipped: number; converted: number } {
    const isGround = options.ground ?? ((mesh: THREE.Mesh) => mesh.userData.surface !== undefined);
    const isExcluded = options.exclude ?? ((object: THREE.Object3D) => !!object.userData.mrMaskExempt);
    let masked = 0;
    let skipped = 0;
    let converted = 0;

    root.traverse((object: THREE.Object3D) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || isExcluded(mesh)) return;
      const ground = isGround(mesh);
      const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const next: THREE.Material[] = [];
      let replaced = false;

      for (const mat of list) {
        if (!mat) { next.push(mat); continue; }
        let target = mat;
        if (options.convert && !(mat as any).isNodeMaterial) {
          target = toNodeMaterial(mat);
          if (target !== mat) { converted++; replaced = true; }
        }
        if ((target as any).isNodeMaterial) {
          // Counts the ones this call newly masked; already-masked materials
          // early-return inside applyToMaterial and are counted as masked too,
          // which is what a caller polling this every second wants to see.
          this.applyToMaterial(target, { ground });
          masked++;
        } else {
          skipped++;
        }
        next.push(target);
      }

      if (replaced) {
        mesh.material = Array.isArray(mesh.material) ? next : next[0];
      }
    });

    return { masked, skipped, converted };
  }

  /** Point the mask at the anchored tee. Call on (re)anchor, not per frame. */
  update(anchorPosition: THREE.Vector3, forward: THREE.Vector3) {
    this.uAnchor.value.set(anchorPosition.x, anchorPosition.z);
    this.uAnchorY.value = anchorPosition.y;
    const len = Math.hypot(forward.x, forward.z) || 1;
    this.uFwd.value.set(forward.x / len, forward.z / len);
  }

  setEnabled(enabled: boolean) {
    this.uEnabled.value = enabled ? 1 : 0;
  }

  /**
   * Frame the world into a window (or null to return to the infinite portal).
   * The window plane doubles as the portal plane, so callers should keep
   * uPortalDist equal to rect.dist while a window is active.
   */
  setWindow(rect: WindowRect | null) {
    if (!rect) {
      this.uWindowEnabled.value = 0;
      return;
    }
    this.uWindowEnabled.value = 1;
    this.uWinDist.value = rect.dist;
    this.uWinLeft.value = rect.left;
    this.uWinRight.value = rect.right;
    this.uWinTop.value = rect.top;
  }

  get windowEnabled(): boolean {
    return this.uWindowEnabled.value === 1;
  }

  /** Eye midpoint, reference space — every frame while presenting (and the
   *  desktop camera in the sim preview, which is what makes parallax testable
   *  on a PC). Cheap: one uniform write. */
  setEye(position: THREE.Vector3) {
    this.uEye.value.copy(position);
  }

  /** Dev aid: replace colorNode with the mask (red = hidden, green = visible). */
  debugColorize(on: boolean) {
    for (const mat of this.#applied) {
      const m: any = mat;
      if (on && !('mrMaskDebugPrevColor' in m.userData)) {
        m.userData.mrMaskDebugPrevColor = m.colorNode ?? null;
        const mask: any = this.maskNode(!!m.userData.mrMaskGround);
        m.colorNode = vec4(mask.oneMinus(), mask, float(0), float(1));
        m.needsUpdate = true;
      } else if (!on && 'mrMaskDebugPrevColor' in m.userData) {
        m.colorNode = m.userData.mrMaskDebugPrevColor ?? null;
        delete m.userData.mrMaskDebugPrevColor;
        m.needsUpdate = true;
      }
    }
  }
}
